// How the phone reads the answer to one media ingest request (BL-2815, ADR 070 — Ingest Protocol § 4):
// pure, status code and body in, what the lot's queued photo does next out.
import { isRecord } from './change-queue-entry';
import { replayErrorOf, type ReplayError } from './change-queue-outcome';

/** What the operator reads when the thumbnail of a file attached by its path is not in storage. */
const NO_PREVIEW_ON_SERVER = 'This photo or video has no preview on the server. Add it again.';

/** What one ingest request's answer means for the queued photo or video. */
export type MediaIngestOutcome =
  /** It landed, or had already landed (`repeat`). */
  | { kind: 'synced'; repeat: boolean }
  /** `MEDIA_REMOVED`: another device removed this id after it was queued. The add is dropped. */
  | { kind: 'dropped' }
  /** `404`: the lot is gone from the server (deleted, or not this tenant's). Its queued files can never be sent. */
  | { kind: 'lot-gone'; error: ReplayError }
  /** The server said no for its own reason (a bad photo, a taken id). Retry or Discard. */
  | { kind: 'failed'; error: ReplayError }
  /** A server error: the lot stays queued and is sent again on a later pass. */
  | { kind: 'retry-later'; error: ReplayError }
  /** The session expired: stop the pass and leave the lot as it was. */
  | { kind: 'transport'; reason: 'unauthorized' };

/**
 * Classify one ingest request's answer.
 *
 * @param input.status - the HTTP status.
 * @param input.body - the parsed JSON body, or null when there was none.
 * @returns what the queue does with the queued add.
 */
export function classifyIngestResponse(input: { status: number; body: unknown }): MediaIngestOutcome {
  const { status } = input;
  const body = isRecord(input.body) ? input.body : {};
  if (status >= 200 && status < 300) {
    return { kind: 'synced', repeat: isRecord(body.data) && body.data.repeat === true };
  }
  if (status === 401) return { kind: 'transport', reason: 'unauthorized' };
  if (status === 410 && body.code === 'MEDIA_REMOVED') return { kind: 'dropped' };
  if (status === 404) return { kind: 'lot-gone', error: replayErrorOf(status, body) };
  if (status >= 500) return { kind: 'retry-later', error: replayErrorOf(status, body) };
  // BL-2816: a photo or video attached by its path whose thumbnail never reached storage (its upload was
  // refused, or an earlier build queued it). The server's words name a file; the operator needs the fix.
  if (status === 400 && isRecord(body.fieldErrors) && typeof body.fieldErrors.posterStoragePath === 'string') {
    return { kind: 'failed', error: { message: NO_PREVIEW_ON_SERVER, code: null, httpStatus: status, fieldNames: [] } };
  }
  return { kind: 'failed', error: replayErrorOf(status, body) };
}
